'use client';
import React, { useState } from 'react';
import { useModel } from '@/context/ModelContext';
import { computeModel, SCENARIO_DRIVERS, fmtCrShort, fmtPct } from '@/lib/financial-model';
import ScenarioComparisonChart from './ScenarioComparisonChart';
import { TrendingDown, Minus, TrendingUp, Info } from 'lucide-react';

type ScenarioKey = 'downside' | 'base' | 'upside';

const SCENARIO_META: Record<
  ScenarioKey,
  { label: string; icon: React.ReactNode; colorClass: string; bgClass: string; borderClass: string }
> = {
  downside: {
    label: 'Downside',
    icon: <TrendingDown size={14} />,
    colorClass: 'text-danger',
    bgClass: 'bg-danger-bg',
    borderClass: 'border-red-200',
  },
  base: {
    label: 'Base Case',
    icon: <Minus size={14} />,
    colorClass: 'text-primary',
    bgClass: 'bg-success-bg',
    borderClass: 'border-green-200',
  },
  upside: {
    label: 'Upside',
    icon: <TrendingUp size={14} />,
    colorClass: 'text-accent',
    bgClass: 'bg-info-bg',
    borderClass: 'border-blue-200',
  },
};

export default function ScenariosPanel() {
  const { assumptions } = useModel();
  const [editableDrivers, setEditableDrivers] = useState({ ...SCENARIO_DRIVERS });

  function updateDriver(
    scenario: ScenarioKey,
    key: keyof typeof SCENARIO_DRIVERS.base,
    value: number
  ) {
    setEditableDrivers((prev) => ({
      ...prev,
      [scenario]: { ...prev[scenario], [key]: value },
    }));
  }

  // Compute each scenario model with editable drivers
  const scenarioModels = (['downside', 'base', 'upside'] as ScenarioKey[]).map((s) => {
    const drivers = editableDrivers[s];
    const scenarioAssumptions = {
      ...assumptions,
      sellingPrice: drivers.sellingPrice,
      year1Utilisation: drivers.year1Utilisation,
      year2PlusUtilisation: drivers.year2PlusUtilisation,
      rawMaterialCost: drivers.rawMaterialCost,
      powerCost: drivers.powerCost,
      machineryCapex: Math.round(
        assumptions.machineryCapex *
          (drivers.totalProjectCost /
            (assumptions.machineryCapex +
              assumptions.civilCapex +
              assumptions.electricalCapex +
              assumptions.contingencyCapex +
              assumptions.workingCapital))
      ),
      civilCapex: Math.round(
        assumptions.civilCapex *
          (drivers.totalProjectCost /
            (assumptions.machineryCapex +
              assumptions.civilCapex +
              assumptions.electricalCapex +
              assumptions.contingencyCapex +
              assumptions.workingCapital))
      ),
      electricalCapex: Math.round(
        assumptions.electricalCapex *
          (drivers.totalProjectCost /
            (assumptions.machineryCapex +
              assumptions.civilCapex +
              assumptions.electricalCapex +
              assumptions.contingencyCapex +
              assumptions.workingCapital))
      ),
      contingencyCapex: Math.round(
        assumptions.contingencyCapex *
          (drivers.totalProjectCost /
            (assumptions.machineryCapex +
              assumptions.civilCapex +
              assumptions.electricalCapex +
              assumptions.contingencyCapex +
              assumptions.workingCapital))
      ),
      workingCapital: Math.round(
        assumptions.workingCapital *
          (drivers.totalProjectCost /
            (assumptions.machineryCapex +
              assumptions.civilCapex +
              assumptions.electricalCapex +
              assumptions.contingencyCapex +
              assumptions.workingCapital))
      ),
      equityAmount: Math.round(drivers.totalProjectCost * 0.4),
      debtAmount: Math.round(drivers.totalProjectCost * 0.6),
    };
    const m = computeModel(scenarioAssumptions);
    return { key: s, model: m };
  });

  const driverFields: Array<{
    key: keyof typeof SCENARIO_DRIVERS.base;
    label: string;
    unit: string;
    isPercent?: boolean;
    step: number;
    min: number;
    max: number;
  }> = [
    { key: 'sellingPrice', label: 'Selling Price', unit: '₹/t', step: 100, min: 4000, max: 15000 },
    {
      key: 'year1Utilisation',
      label: 'Year-1 Utilisation',
      unit: '%',
      isPercent: true,
      step: 1,
      min: 30,
      max: 100,
    },
    {
      key: 'year2PlusUtilisation',
      label: 'Steady Utilisation',
      unit: '%',
      isPercent: true,
      step: 1,
      min: 50,
      max: 100,
    },
    {
      key: 'rawMaterialCost',
      label: 'Raw Material Cost',
      unit: '₹/t',
      step: 50,
      min: 1000,
      max: 5000,
    },
    { key: 'powerCost', label: 'Power Cost', unit: '₹/t', step: 50, min: 400, max: 3000 },
    {
      key: 'totalProjectCost',
      label: 'Total Project Cost',
      unit: '₹',
      step: 1000000,
      min: 30000000,
      max: 150000000,
    },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-base font-semibold text-foreground">
          Scenario Analysis — Downside / Base / Upside
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Edit scenario drivers below — all metrics recalculate instantly
        </p>
      </div>

      {/* Editable scenario drivers */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-muted border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">
            Key Driver Assumptions by Scenario
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Yellow cells are editable — changes recalculate scenario outputs below
          </p>
        </div>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-xs" style={{ minWidth: '600px' }}>
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left py-2.5 px-4 font-semibold text-muted-foreground min-w-[180px]">
                  Driver
                </th>
                {(['downside', 'base', 'upside'] as ScenarioKey[]).map((s) => (
                  <th
                    key={`scenario-th-${s}`}
                    className={`py-2.5 px-4 text-center font-semibold ${SCENARIO_META[s].colorClass}`}
                  >
                    <div className="flex items-center justify-center gap-1">
                      {SCENARIO_META[s].icon}
                      {SCENARIO_META[s].label}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {driverFields.map((field) => (
                <tr
                  key={`driver-row-${field.key}`}
                  className="border-b border-border/50 hover:bg-muted/20 transition-colors"
                >
                  <td className="py-2.5 px-4 font-medium text-foreground">
                    {field.label}
                    <span className="text-muted-foreground ml-1">({field.unit})</span>
                  </td>
                  {(['downside', 'base', 'upside'] as ScenarioKey[]).map((s) => {
                    const rawVal = editableDrivers[s][field.key];
                    const displayVal = field.isPercent ? rawVal * 100 : rawVal;
                    return (
                      <td key={`driver-cell-${field.key}-${s}`} className="py-2 px-4 text-center">
                        <input
                          type="number"
                          className="input-editable text-center w-full max-w-[120px] mx-auto block"
                          value={displayVal}
                          step={field.step}
                          min={field.min}
                          max={field.max}
                          onChange={(e) => {
                            const parsed = parseFloat(e.target.value);
                            if (!isNaN(parsed)) {
                              updateDriver(s, field.key, field.isPercent ? parsed / 100 : parsed);
                            }
                          }}
                          aria-label={`${field.label} — ${SCENARIO_META[s].label}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Scenario results comparison */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {scenarioModels.map(({ key, model: m }) => {
          const meta = SCENARIO_META[key];
          const yr1 = m.years[1];
          const yr2 = m.years[2];
          return (
            <div
              key={`scenario-result-${key}`}
              className={`rounded-xl border p-4 ${meta.bgClass} ${meta.borderClass}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className={meta.colorClass}>{meta.icon}</span>
                <h3 className={`text-sm font-semibold ${meta.colorClass}`}>{meta.label}</h3>
              </div>
              <div className="space-y-2.5">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">Project Cost</span>
                  <span className="font-mono text-xs font-semibold text-foreground">
                    {fmtCrShort(m.totalProjectCost)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">Year 1 Revenue</span>
                  <span className="font-mono text-xs font-semibold text-foreground">
                    {fmtCrShort(yr1?.revenue ?? 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">Year 2 Revenue</span>
                  <span className="font-mono text-xs font-semibold text-foreground">
                    {fmtCrShort(yr2?.revenue ?? 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">Year 2 Gross Profit</span>
                  <span className="font-mono text-xs font-semibold text-foreground">
                    {fmtCrShort(yr2?.grossProfit ?? 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">Year 2 EBITDA</span>
                  <span className={`font-mono text-xs font-semibold ${meta.colorClass}`}>
                    {fmtCrShort(yr2?.ebitda ?? 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">Year 2 PAT</span>
                  <span className={`font-mono text-xs font-semibold ${meta.colorClass}`}>
                    {fmtCrShort(yr2?.pat ?? 0)}
                  </span>
                </div>
                <div className="h-px bg-current opacity-10 my-1" />
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">PAT Margin (Yr2)</span>
                  <span className={`font-mono text-xs font-semibold ${meta.colorClass}`}>
                    {fmtPct(yr2?.patMarginPct ?? 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">EBITDA Margin (Yr2)</span>
                  <span className={`font-mono text-xs font-semibold ${meta.colorClass}`}>
                    {fmtPct(yr2?.ebitdaMarginPct ?? 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">Project IRR</span>
                  <span className={`font-mono text-sm font-bold ${meta.colorClass}`}>
                    {(m.projectIRR * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">NPV @ 14%</span>
                  <span className={`font-mono text-xs font-semibold ${meta.colorClass}`}>
                    {fmtCrShort(m.projectNPV)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">Payback</span>
                  <span className="font-mono text-xs font-semibold text-foreground">
                    {m.paybackYearFractional > 0
                      ? `Yr ${m.paybackYearFractional.toFixed(1)}`
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Scenario comparison chart */}
      <div className="bg-card border border-border rounded-xl p-5">
        <h3 className="text-sm font-semibold text-foreground mb-1">
          Scenario Comparison — Year 2 Key Metrics
        </h3>
        <p className="text-xs text-muted-foreground mb-4">
          Revenue, EBITDA, PAT across three scenarios · ₹ Crores
        </p>
        <ScenarioComparisonChart scenarioModels={scenarioModels} />
      </div>

      {/* Scenario interpretations */}
      <div className="space-y-3">
        {[
          {
            key: 'base' as ScenarioKey,
            text: 'Base Case produces strong and bankable cash flows from Year 1 with DSCR comfortably > 1.5x.',
          },
          {
            key: 'downside' as ScenarioKey,
            text: 'Downside (price ₹7,000 + lower util + higher RM) still generates positive EBITDA and positive PAT, but thinner coverage — mitigate with offtake contracts.',
          },
          {
            key: 'upside' as ScenarioKey,
            text: 'Upside (price ₹9,000 + full util + lower costs) is highly attractive and achievable if NTPC/TPP volumes lock and feedstock is well managed. Carbon credits (₹3–5 Cr/year) remain pure upside.',
          },
        ].map((item) => {
          const meta = SCENARIO_META[item.key];
          return (
            <div
              key={`scenario-interp-${item.key}`}
              className={`flex items-start gap-3 p-3 rounded-lg border ${meta.bgClass} ${meta.borderClass}`}
            >
              <span className={meta.colorClass}>{meta.icon}</span>
              <p className="text-xs text-foreground/80 leading-relaxed">{item.text}</p>
            </div>
          );
        })}
        <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg border border-border">
          <Info size={13} className="text-muted-foreground mt-0.5 flex-shrink-0" />
          <p className="text-xs text-muted-foreground">
            At 5 TPH the project has better economies of scale than 2 TPH on fixed costs and labour
            per tonne. Carbon credits (potential ₹3–5 Cr/year at scale) and any MNRE CFA remain pure
            upside not required for Base Case viability.
          </p>
        </div>
      </div>
    </div>
  );
}
